/**
 * Single frontend source of truth for plan tiers, prices and features.
 *
 * Prices (cents) mirror `PaymentService/code/src/utils/stripe-client.ts`
 * (`STRIPE_PLANS`) — that file is the backend source of truth for what
 * Stripe actually charges. Keep both in sync when prices change.
 */
import { formatPrice } from '../utils/formatPrice';

export type PaidTier = 'pro' | 'premium';
export type Tier = 'free' | PaidTier;
export type DurationMonths = 1 | 3 | 6 | 12;

export interface PlanPrice {
  planId: string;
  months: DurationMonths;
  totalCents: number;
  perMonthCents: number;
}

export interface PlanFeature {
  label: string;
  included: boolean;
  note?: string;
}

export interface Plan {
  tier: Tier;
  name: string;
  tagline: string;
  maxSearches: number;
  maxZipCodes: number | null;
  filtersApplied: boolean;
  emailChannel: boolean;
  priority: boolean;
  prices: PlanPrice[];
  features: PlanFeature[];
}

const PRO_PRICES: PlanPrice[] = [
  { planId: 'pro-1m', months: 1, totalCents: 599, perMonthCents: 599 },
  { planId: 'pro-3m', months: 3, totalCents: 1499, perMonthCents: 500 },
  { planId: 'pro-6m', months: 6, totalCents: 2499, perMonthCents: 417 },
  { planId: 'pro-12m', months: 12, totalCents: 3999, perMonthCents: 333 },
];

const PREMIUM_PRICES: PlanPrice[] = [
  { planId: 'premium-1m', months: 1, totalCents: 999, perMonthCents: 999 },
  { planId: 'premium-3m', months: 3, totalCents: 2499, perMonthCents: 833 },
  { planId: 'premium-6m', months: 6, totalCents: 3999, perMonthCents: 667 },
  { planId: 'premium-12m', months: 12, totalCents: 5999, perMonthCents: 500 },
];

export const PLANS: Record<Tier, Plan> = {
  free: {
    tier: 'free',
    name: 'Free',
    tagline: '14 Tage kostenlos testen',
    maxSearches: 1,
    maxZipCodes: 15,
    filtersApplied: false,
    emailChannel: false,
    priority: false,
    prices: [],
    features: [
      { label: '1 Suche, 14 Tage aktiv', included: true },
      { label: 'Telegram + Push', included: true },
      { label: '20+ Portale', included: true },
      { label: 'Filter einstellen', included: true, note: 'angewendet ab Pro' },
      { label: 'E-Mail-Benachrichtigungen', included: false },
      { label: 'Listings vollständig sichtbar', included: false },
    ],
  },
  pro: {
    tier: 'pro',
    name: 'Pro',
    tagline: 'Für die aktive Wohnungssuche',
    maxSearches: 3,
    maxZipCodes: 20,
    filtersApplied: true,
    emailChannel: true,
    priority: false,
    prices: PRO_PRICES,
    features: [
      { label: '3 Suchen gleichzeitig', included: true },
      { label: 'Suche läuft nicht nach 14 Tagen ab', included: true },
      { label: 'Telegram + Push + E-Mail', included: true },
      { label: '20+ Portale', included: true },
      { label: 'Alle Filter', included: true },
      { label: '20 PLZ pro Suche', included: true },
      { label: 'Alle Listings klar sichtbar', included: true },
    ],
  },
  premium: {
    tier: 'premium',
    name: 'Premium',
    tagline: 'Maximale Reichweite, höchste Priorität',
    maxSearches: 10,
    maxZipCodes: 200,
    filtersApplied: true,
    emailChannel: true,
    priority: true,
    prices: PREMIUM_PRICES,
    features: [
      { label: '10 Suchen gleichzeitig', included: true },
      { label: 'Suche läuft nicht nach 14 Tagen ab', included: true },
      { label: 'Alle Kanäle', included: true },
      { label: '20+ Portale', included: true },
      { label: 'Alle Filter', included: true },
      { label: 'Bis 200 PLZ pro Suche', included: true },
      { label: 'Gruppen-Chats', included: true },
      { label: 'Prioritäts-Benachrichtigungen', included: true },
    ],
  },
};

export function getPlanPrice(tier: PaidTier, months: DurationMonths): PlanPrice {
  const price = PLANS[tier].prices.find((p) => p.months === months);
  if (!price) {
    throw new Error(`No price configured for ${tier} / ${months} months`);
  }
  return price;
}

/** The 12-month option — always the cheapest per-month price for a tier. */
export function cheapestPerMonth(tier: PaidTier): PlanPrice {
  return getPlanPrice(tier, 12);
}

/** F16: "5,99 € im Monat, im Jahrespaket 3,33 €" */
export function monthlyFromLabel(tier: PaidTier): string {
  const monthly = getPlanPrice(tier, 1);
  const yearly = cheapestPerMonth(tier);
  return `${formatPrice(monthly.totalCents)} im Monat, im Jahrespaket ${formatPrice(yearly.perMonthCents)}`;
}
